import type { Job } from '@/lib/schemas/db'

export type JobNumberTagConflict = {
  tag: string
  conflictingJob?: Job
  message: string
}

const NON_BLOCKING_NUMBER_TAG_STATUSES = new Set<Job['status']>([
  'ReadyToInvoice',
  'Invoiced',
  'Cancelled',
])

export function normalizeJobNumberTag(value: string | null | undefined) {
  return String(value ?? '').trim()
}

export function doesJobNumberTagBlockReuse(job: Pick<Job, 'status'>) {
  return !NON_BLOCKING_NUMBER_TAG_STATUSES.has(job.status)
}

export function findJobNumberTagConflict(
  jobs: Job[],
  currentJobId: string,
  nextTag: string,
): JobNumberTagConflict | null {
  const normalizedTag = normalizeJobNumberTag(nextTag)
  if (!normalizedTag) return null

  const conflictingJob = jobs.find((job) =>
    job.id !== currentJobId &&
    doesJobNumberTagBlockReuse(job) &&
    normalizeJobNumberTag(job.numberTag) === normalizedTag,
  )

  if (!conflictingJob) return null

  return {
    tag: normalizedTag,
    conflictingJob,
    message: `Number tag ${normalizedTag} is already assigned to another job.`,
  }
}

export function isPocketBaseJobNumberTagUniqueError(error: unknown) {
  if (!error || typeof error !== 'object') return false

  const message =
    typeof (error as { message?: unknown }).message === 'string'
      ? String((error as { message?: string }).message)
      : typeof (error as { originalError?: { message?: unknown } }).originalError?.message === 'string'
        ? String((error as { originalError?: { message?: string } }).originalError?.message)
        : ''

  if (/idx_jobs_number_tag_unique/i.test(message)) return true
  if (/unique constraint failed:\s*jobs\.number_tag/i.test(message)) return true

  const responseData = (error as { response?: { data?: unknown } }).response?.data
  if (responseData && typeof responseData === 'object') {
    const fieldError = (responseData as { number_tag?: { code?: unknown; message?: unknown } }).number_tag
    if (fieldError?.code === 'validation_not_unique') return true
    if (typeof fieldError?.message === 'string' && /unique/i.test(fieldError.message)) return true
  }

  return false
}
